// src/utils/browserNotifications.js
/**
 * Sistema completo de notificações para navegador
 * Versão ERP - Suporte a múltiplos módulos
 * 
 * CHANGELOG:
 * v3.2 - Added chat notification deduplication with cooldown
 * v3.1 - Added message hash tracking for duplicate prevention
 * v3.0 - Initial modular system
 */

import {
    getModuleConfig,
    getModuleDefaultRoute,
    PRIORITY_COLORS,
    NOTIFICATION_MODULES,
    ROLE_MODULE_MAP
} from '../config/notificationConfig';

// ============================================================
// DEDUPLICATION CACHE FOR CHAT NOTIFICATIONS
// ============================================================

/**
 * Cache for chat notifications to prevent duplicates
 * Key: message hash (channelId + senderName + message preview)
 * Value: timestamp of when notification was last sent
 */
const chatNotificationCache = new Map();

/**
 * Cache for all notifications to prevent rapid duplicates
 * Key: notification tag or ID
 * Value: timestamp of when notification was last sent
 */
const notificationCooldownCache = new Map();

/**
 * Clean up old entries from caches
 * Removes entries older than 60 seconds
 */
const cleanNotificationCaches = () => {
    const now = Date.now();
    const maxAge = 60000; // 60 seconds

    // Clean chat cache
    for (const [key, time] of chatNotificationCache.entries()) {
        if (now - time > maxAge) {
            chatNotificationCache.delete(key);
        }
    }

    // Clean cooldown cache
    for (const [key, time] of notificationCooldownCache.entries()) {
        if (now - time > maxAge) {
            notificationCooldownCache.delete(key);
        }
    }
};

// Run cache cleanup every 30 seconds
setInterval(cleanNotificationCaches, 30000);

// ============================================================
// MAPPINGS
// ============================================================

// Criar mapeamentos a partir do NOTIFICATION_MODULES
const MODULE_ICON_MAP = {};
const MODULE_COLOR_MAP = {};
const MODULE_NAME_MAP = {};

// Popular os mapeamentos com os dados do NOTIFICATION_MODULES
Object.entries(NOTIFICATION_MODULES || {}).forEach(([key, module]) => {
    MODULE_ICON_MAP[key] = module.icon || '📌';
    MODULE_COLOR_MAP[key] = module.color || '#6c757d';
    MODULE_NAME_MAP[key] = module.name || key;
});

// Mapeamento de prioridade para emojis
const PRIORITY_EMOJIS = {
    'Crítico': '🚨',
    'Alta': '🔴',
    'Média': '🟡',
    'Baixa': '🟢'
};

// ============================================================
// SOUND HELPER
// ============================================================

// 🔊 Helper para tocar som de notificação
const playNotificationSound = () => {
    try {
        const audio = new Audio('/sounds/soft_chime.mp3');
        audio.play().catch((err) => {
            // Browsers may block audio autoplay if the user has not interacted with the page yet
            console.warn('⚠️ Sound playback prevented or failed:', err);
        });
    } catch (error) {
        console.error('❌ Error playing notification sound:', error);
    }
};

// ============================================================
// CORE FUNCTIONS
// ============================================================

/**
 * Verifica se o navegador suporta notificações
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
    return "Notification" in window;
};

/**
 * Solicita permissão para notificações
 * @param {boolean} showPrompt - Se deve mostrar o prompt ao usuário
 * @returns {Promise<boolean>} - true se permissão concedida
 */
export const requestNotificationPermission = async (showPrompt = true) => {
    if (!isNotificationSupported()) {
        console.warn('⚠️ Navegador não suporta notificações');
        return false;
    }

    // Se já tem permissão, retorna true
    if (Notification.permission === "granted") {
        return true;
    }

    // Se foi negado, não solicita novamente
    if (Notification.permission === "denied") {
        console.warn('⚠️ Permissão de notificação negada permanentemente');
        return false;
    }

    // Se está como 'default' e deve mostrar prompt
    if (showPrompt && Notification.permission === "default") {
        try {
            const permission = await Notification.requestPermission();
            console.log(`📊 Permissão de notificação: ${permission}`);
            return permission === "granted";
        } catch (error) {
            console.error('❌ Erro ao solicitar permissão:', error);
            return false;
        }
    }

    return false;
};

/**
 * Envia uma notificação para o navegador
 * @param {string} title - Título da notificação
 * @param {string} body - Corpo da notificação
 * @param {string} onClickUrl - URL para redirecionar ao clicar
 * @param {Object} options - Opções adicionais
 * @returns {Notification|null}
 */
export const sendBrowserNotification = (title, body, onClickUrl = null, options = {}) => {
    // Verificar se o navegador suporta notificações
    if (!isNotificationSupported()) {
        console.warn('⚠️ Notificações não suportadas');
        return null;
    }

    // Verificar permissão
    if (Notification.permission !== "granted") {
        console.warn('⚠️ Permissão de notificação não concedida');
        return null;
    }

    // ============================================================
    // DEDUPLICATION CHECK FOR BASIC NOTIFICATIONS
    // ============================================================

    const dedupeKey = options.tag || `notification_${title}_${body?.substring(0, 30)}`;
    const now = Date.now();

    if (notificationCooldownCache.has(dedupeKey)) {
        const lastSent = notificationCooldownCache.get(dedupeKey);
        if (now - lastSent < 3000) { // 3 second cooldown
            console.log(`🛑 Duplicate notification blocked: ${dedupeKey}`);
            return null;
        }
    }
    notificationCooldownCache.set(dedupeKey, now);

    try {
        // Opções padrão da notificação
        const defaultOptions = {
            body: body || '',
            icon: options.icon || '/favicon.ico',
            badge: options.badge || '/favicon.ico',
            tag: options.tag || `notification_${Date.now()}`,
            requireInteraction: options.requireInteraction !== undefined ? options.requireInteraction : true,
            silent: options.silent || false,
            vibrate: options.vibrate || [200, 100, 200],
            timestamp: Date.now(),
            actions: options.actions || [
                {
                    action: 'open',
                    title: '📖 Abrir',
                    icon: '/favicon.ico'
                },
                {
                    action: 'close',
                    title: '❌ Fechar',
                    icon: '/favicon.ico'
                }
            ],
            data: options.data || {}
        };

        // Criar a notificação
        const notification = new Notification(title, { ...defaultOptions, ...options });

        // 🔊 Tocar som se não for silencioso
        if (!options.silent) {
            playNotificationSound();
        }

        // Configurar evento de clique
        if (onClickUrl) {
            notification.onclick = (event) => {
                event.preventDefault();
                notification.close();

                // Focar na janela existente ou abrir nova
                if (window.focus) {
                    window.focus();
                }

                // Navegar para a URL
                window.location.href = onClickUrl;
            };
        }

        // Configurar evento de fechamento
        notification.onclose = () => {
            console.log('🔕 Notificação fechada');
        };

        // Configurar evento de erro
        notification.onerror = (error) => {
            console.error('❌ Erro na notificação:', error);
        };

        // Auto-fechar após 15 segundos (opcional)
        setTimeout(() => {
            try {
                if (notification && !notification.closed) {
                    notification.close();
                }
            } catch (e) {
                // Ignorar erro ao fechar
            }
        }, 15000);

        return notification;
    } catch (error) {
        console.error('❌ Erro ao enviar notificação:', error);
        return null;
    }
};

// ============================================================
// MODULE NOTIFICATION
// ============================================================

/**
 * Envia uma notificação para um módulo específico do ERP
 * @param {string} moduleId - ID do módulo (helpdesk, finance, inventory, etc)
 * @param {string} title - Título da notificação
 * @param {string} body - Corpo da notificação
 * @param {Object} options - Opções adicionais
 * @param {string} options.route - Rota para redirecionar
 * @param {string} options.priority - Prioridade (Crítico, Alta, Média, Baixa)
 * @param {Object} options.data - Dados adicionais
 * @param {string} options.tag - Tag para agrupamento
 * @param {string} options.icon - Ícone personalizado
 * @param {boolean} options.requireInteraction - Se deve requerer interação
 * @param {number} options.autoClose - Tempo em ms para auto-fechar
 * @param {boolean} options.silent - Se deve ser silenciosa (não tocar som)
 * @param {string} options.dedupeKey - Chave personalizada para deduplicação
 * @returns {Notification|null}
 */
export const sendModuleNotification = (moduleId, title, body, options = {}) => {
    const config = getModuleConfig(moduleId);
    const moduleIcon = config?.icon || MODULE_ICON_MAP[moduleId] || '📌';
    const moduleColor = config?.color || MODULE_COLOR_MAP[moduleId] || '#6c757d';
    const moduleName = config?.name || MODULE_NAME_MAP[moduleId] || moduleId || 'Módulo';

    // Verificar suporte e permissão
    if (!isNotificationSupported() || Notification.permission !== "granted") {
        console.warn(`⚠️ Notificações não suportadas ou sem permissão para ${moduleId}`);
        return null;
    }

    // ============================================================
    // DEDUPLICATION CHECK FOR MODULE NOTIFICATIONS
    // ============================================================

    const dedupeKey = options.dedupeKey || options.tag || `module_${moduleId}_${title}_${body?.substring(0, 30)}`;
    const now = Date.now();

    if (notificationCooldownCache.has(dedupeKey)) {
        const lastSent = notificationCooldownCache.get(dedupeKey);
        if (now - lastSent < 3000) { // 3 second cooldown
            console.log(`🛑 Duplicate module notification blocked: ${dedupeKey}`);
            return null;
        }
    }
    notificationCooldownCache.set(dedupeKey, now);

    try {
        // Preparar ícone baseado no módulo
        const icon = options.icon || moduleIcon || '/favicon.ico';
        const priorityEmoji = options.priority ? PRIORITY_EMOJIS[options.priority] : '';
        const priorityColor = options.priority ? PRIORITY_COLORS[options.priority] : null;

        // Criar a notificação
        const notificationOptions = {
            body: body || '',
            icon: icon,
            badge: icon,
            tag: options.tag || `erp_${moduleId}_${Date.now()}`,
            requireInteraction: options.requireInteraction !== undefined ? options.requireInteraction : true,
            silent: options.silent || false,
            vibrate: options.vibrate || [200, 100, 200],
            timestamp: Date.now(),
            data: {
                module: moduleId,
                moduleName: moduleName,
                moduleColor: moduleColor,
                route: options.route || getModuleDefaultRoute(moduleId),
                priority: options.priority || 'Média',
                ...options.data
            }
        };

        // Adicionar badge de prioridade se disponível
        if (priorityColor) {
            notificationOptions.data.priorityColor = priorityColor;
        }

        const notificationTitle = `${priorityEmoji || moduleIcon} ${title}`;
        const notification = new Notification(notificationTitle, notificationOptions);

        // 🔊 Tocar o som de notificação (a menos que seja silencioso)
        if (!options.silent) {
            playNotificationSound();
        }

        // Configurar clique
        notification.onclick = (event) => {
            event.preventDefault();
            notification.close();

            if (window.focus) window.focus();

            const route = options.route || getModuleDefaultRoute(moduleId);
            if (route) {
                window.location.href = route;
            }
        };

        // Configurar evento de fechamento
        notification.onclose = () => {
            console.log(`🔕 Notificação ${moduleId} fechada`);
        };

        // Configurar evento de erro
        notification.onerror = (error) => {
            console.error(`❌ Erro na notificação ${moduleId}:`, error);
        };

        // Auto-fechar após tempo configurável
        const autoCloseDelay = options.autoClose || 15000;
        setTimeout(() => {
            try {
                if (notification && !notification.closed) {
                    notification.close();
                }
            } catch (e) {
                // Ignorar erro ao fechar
            }
        }, autoCloseDelay);

        return notification;
    } catch (error) {
        console.error(`❌ Erro ao enviar notificação para ${moduleId}:`, error);
        return null;
    }
};

// ============================================================
// TEST NOTIFICATION
// ============================================================

/**
 * Envia uma notificação de teste
 * @param {string} title - Título personalizado
 * @param {string} body - Corpo personalizado
 * @param {string} moduleId - Módulo para testar
 * @returns {Notification|null}
 */
export const sendTestNotification = (title = '🔔 Notificação de Teste', body = 'Esta é uma notificação de teste do sistema!', moduleId = 'helpdesk') => {
    return sendModuleNotification(moduleId, title, body, {
        route: window.location.pathname,
        priority: 'Média',
        requireInteraction: true,
        dedupeKey: `test_${Date.now()}`
    });
};

// ============================================================
// HELPDESK NOTIFICATIONS
// ============================================================

/**
 * Envia notificação de novo ticket (mantido para compatibilidade)
 * @param {string} ticketId - ID do ticket
 * @param {string} ticketTitle - Título do ticket
 * @param {string} userName - Nome do usuário que criou
 * @param {string} priority - Prioridade do ticket
 * @returns {Notification|null}
 */
export const sendNewTicketNotification = (ticketId, ticketTitle, userName, priority = 'media') => {
    const priorityMap = {
        'critico': 'Crítico',
        'alto': 'Alta',
        'medio': 'Média',
        'baixo': 'Baixa'
    };

    const priorityLabel = priorityMap[priority] || 'Média';
    const shortId = ticketId?.substring(0, 8) || 'unknown';

    return sendModuleNotification(
        'helpdesk',
        `🎫 Novo Ticket #${shortId}`,
        `${userName || 'Usuário'} abriu: "${(ticketTitle || '').substring(0, 60)}${(ticketTitle || '').length > 60 ? '...' : ''}"`,
        {
            route: `/helpdesk/gerenciamento-de-tickets?ticketId=${ticketId}`,
            tag: `ticket_${ticketId}`,
            priority: priorityLabel,
            dedupeKey: `ticket_${ticketId}`,
            data: { ticketId, ticketTitle, userName }
        }
    );
};

/**
 * Envia notificação de resposta/follow-up
 * @param {string} ticketId - ID do ticket
 * @param {string} ticketTitle - Título do ticket
 * @param {string} responderName - Nome do responsável
 * @param {string} message - Mensagem da resposta
 * @returns {Notification|null}
 */
export const sendFollowUpNotification = (ticketId, ticketTitle, responderName, message) => {
    const shortId = ticketId?.substring(0, 8) || 'unknown';

    return sendModuleNotification(
        'helpdesk',
        `📩 Resposta Ticket #${shortId}`,
        `${responderName || 'Alguém'} respondeu: "${(message || '').substring(0, 80)}${(message || '').length > 80 ? '...' : ''}"`,
        {
            route: `/helpdesk/gerenciamento-de-tickets?ticketId=${ticketId}`,
            tag: `followup_${ticketId}`,
            priority: 'Média',
            dedupeKey: `followup_${ticketId}_${Date.now()}`,
            data: { ticketId, ticketTitle, responderName }
        }
    );
};

/**
 * Envia notificação de ticket fechado
 * @param {string} ticketId - ID do ticket
 * @param {string} ticketTitle - Título do ticket
 * @param {string} resolution - Resolução do ticket
 * @returns {Notification|null}
 */
export const sendTicketClosedNotification = (ticketId, ticketTitle, resolution) => {
    const shortId = ticketId?.substring(0, 8) || 'unknown';

    return sendModuleNotification(
        'helpdesk',
        `✅ Ticket #${shortId} Fechado`,
        `Seu ticket "${(ticketTitle || '').substring(0, 40)}" foi resolvido.${resolution ? ` Motivo: ${resolution}` : ''}`,
        {
            route: `/helpdesk/gerenciamento-de-tickets?ticketId=${ticketId}`,
            tag: `closed_${ticketId}`,
            priority: 'Baixa',
            dedupeKey: `closed_${ticketId}`,
            data: { ticketId, ticketTitle, resolution }
        }
    );
};

/**
 * Envia notificação de ticket atribuído
 * @param {string} ticketId - ID do ticket
 * @param {string} ticketTitle - Título do ticket
 * @param {string} assignedTo - Nome de quem atribuiu
 * @returns {Notification|null}
 */
export const sendTicketAssignedNotification = (ticketId, ticketTitle, assignedTo) => {
    const shortId = ticketId?.substring(0, 8) || 'unknown';

    return sendModuleNotification(
        'helpdesk',
        `📋 Ticket #${shortId} Atribuído`,
        `Você foi designado para: "${(ticketTitle || '').substring(0, 40)}"`,
        {
            route: `/helpdesk/gerenciamento-de-tickets?ticketId=${ticketId}`,
            tag: `assigned_${ticketId}`,
            priority: 'Alta',
            dedupeKey: `assigned_${ticketId}`,
            data: { ticketId, ticketTitle, assignedTo }
        }
    );
};

// ============================================================
// FINANCE NOTIFICATIONS
// ============================================================

/**
 * Envia notificação financeira
 * @param {string} invoiceId - ID da fatura
 * @param {string} customer - Nome do cliente
 * @param {number} amount - Valor da fatura
 * @param {string} status - Status da fatura
 * @param {Date|string} dueDate - Data de vencimento
 * @returns {Notification|null}
 */
export const sendFinanceNotification = (invoiceId, customer, amount, status, dueDate) => {
    if (!invoiceId) return null;

    const isOverdue = status === 'overdue' || (dueDate && new Date(dueDate) < new Date());
    const shortId = invoiceId.substring(0, 8);
    const formattedAmount = amount?.toFixed(2)?.replace('.', ',') || '0,00';

    return sendModuleNotification(
        'finance',
        `${isOverdue ? '⚠️' : '📄'} ${isOverdue ? 'Fatura Vencida' : 'Fatura Pendente'}`,
        `Fatura #${shortId}: ${customer || 'Cliente'} - R$ ${formattedAmount}`,
        {
            route: '/finance/invoices',
            tag: `invoice_${invoiceId}`,
            priority: isOverdue ? 'Crítico' : 'Alta',
            dedupeKey: `invoice_${invoiceId}`,
            data: { invoiceId, customer, amount, status, dueDate }
        }
    );
};

// ============================================================
// INVENTORY NOTIFICATIONS
// ============================================================

/**
 * Envia notificação de estoque baixo
 * @param {string} productId - ID do produto
 * @param {string} productName - Nome do produto
 * @param {number} quantity - Quantidade atual
 * @param {number} minStock - Estoque mínimo
 * @returns {Notification|null}
 */
export const sendInventoryNotification = (productId, productName, quantity, minStock) => {
    if (!productId) return null;

    return sendModuleNotification(
        'inventory',
        '📦 Estoque Baixo',
        `Produto "${(productName || productId).substring(0, 40)}" está com ${quantity || 0} unidades (mínimo: ${minStock || 0})`,
        {
            route: `/inventory/products/${productId}`,
            tag: `stock_${productId}`,
            priority: 'Alta',
            dedupeKey: `stock_${productId}`,
            data: { productId, productName, quantity, minStock }
        }
    );
};

// ============================================================
// CHAT NOTIFICATIONS (WITH DEDUPLICATION)
// ============================================================

/**
 * Envia notificação de chat com deduplicação robusta
 * @param {string} senderName - Nome do remetente
 * @param {string} message - Mensagem
 * @param {string} channelId - ID do canal
 * @param {string} channelName - Nome do canal
 * @returns {Notification|null}
 */
export const sendChatNotification = (senderName, message, channelId, channelName = 'Chat') => {
    if (!senderName || !message) return null;

    // ============================================================
    // DEDUPLICATION: Create a unique key for this message
    // ============================================================

    // Use a combination of channel, sender, and message preview
    const messagePreview = message.substring(0, 30);
    const messageHash = `${channelId || 'chat'}_${senderName}_${messagePreview}`;
    const now = Date.now();

    // Check if this exact message was notified recently
    if (chatNotificationCache.has(messageHash)) {
        const lastNotified = chatNotificationCache.get(messageHash);
        const timeSinceLast = now - lastNotified;

        if (timeSinceLast < 5000) { // 5 second cooldown
            console.log(`🛑 Duplicate chat notification blocked: "${messagePreview}" (${timeSinceLast}ms since last)`);
            return null;
        }
    }

    // Store the notification time
    chatNotificationCache.set(messageHash, now);

    // Also add to global cooldown cache
    const globalKey = `chat_${messageHash}`;
    if (notificationCooldownCache.has(globalKey)) {
        const lastSent = notificationCooldownCache.get(globalKey);
        if (now - lastSent < 3000) {
            return null;
        }
    }
    notificationCooldownCache.set(globalKey, now);

    // Clean up old cache entries
    cleanNotificationCaches();

    console.log(`📨 Sending chat notification: ${senderName} - "${messagePreview}"`);

    return sendModuleNotification(
        'chat',
        `💬 ${senderName}`,
        `${(message || '').substring(0, 80)}${(message || '').length > 80 ? '...' : ''}`,
        {
            route: `/chat?channel=${channelId || ''}`,
            tag: `chat_${channelId || 'general'}_${Date.now()}`,
            priority: 'Média',
            dedupeKey: `chat_${messageHash}`,
            data: {
                senderName,
                channelId,
                message,
                channelName
            },
            requireInteraction: true,
            autoClose: 8000,
            silent: false
        }
    );
};

// ============================================================
// QUALITY NOTIFICATIONS
// ============================================================

/**
 * Envia notificação de qualidade (auditoria, inspeção)
 * @param {string} type - Tipo (audit, inspection, nonconformity)
 * @param {string} title - Título
 * @param {string} description - Descrição
 * @param {string} id - ID do item
 * @returns {Notification|null}
 */
export const sendQualityNotification = (type, title, description, id) => {
    if (!type || !title) return null;

    const emojis = {
        'audit': '🔍',
        'inspection': '🔬',
        'nonconformity': '⚠️',
        'approval': '✅',
        'rejection': '❌'
    };

    const emoji = emojis[type] || '📋';
    const routeMap = {
        'audit': '/quality/audits',
        'inspection': '/quality/inspections',
        'nonconformity': '/quality/nonconformities'
    };

    return sendModuleNotification(
        'quality',
        `${emoji} ${title}`,
        (description || '').substring(0, 80) + ((description || '').length > 80 ? '...' : ''),
        {
            route: routeMap[type] || '/quality',
            tag: `quality_${type}_${id || Date.now()}`,
            priority: type === 'nonconformity' ? 'Crítico' : 'Média',
            dedupeKey: `quality_${type}_${id}`,
            data: { type, title, description, id }
        }
    );
};

// ============================================================
// CRM NOTIFICATIONS
// ============================================================

/**
 * Envia notificação de CRM (lead, oportunidade)
 * @param {string} type - Tipo (lead, opportunity, customer)
 * @param {string} title - Título
 * @param {string} description - Descrição
 * @param {string} id - ID do item
 * @returns {Notification|null}
 */
export const sendCRMNotification = (type, title, description, id) => {
    if (!type || !title) return null;

    const emojis = {
        'lead': '🆕',
        'opportunity': '💎',
        'customer': '🤝',
        'meeting': '📅',
        'task': '✅'
    };

    const emoji = emojis[type] || '📌';
    const routeMap = {
        'lead': '/crm/leads',
        'opportunity': '/crm/opportunities',
        'customer': '/crm/customers',
        'meeting': '/crm/calendar',
        'task': '/crm/tasks'
    };

    return sendModuleNotification(
        'crm',
        `${emoji} ${title}`,
        (description || '').substring(0, 80) + ((description || '').length > 80 ? '...' : ''),
        {
            route: routeMap[type] || '/crm',
            tag: `crm_${type}_${id || Date.now()}`,
            priority: type === 'opportunity' ? 'Alta' : 'Média',
            dedupeKey: `crm_${type}_${id}`,
            data: { type, title, description, id }
        }
    );
};

// ============================================================
// HR NOTIFICATIONS
// ============================================================

/**
 * Envia notificação de RH (recrutamento, férias, ponto)
 * @param {string} type - Tipo (recruitment, vacation, attendance)
 * @param {string} title - Título
 * @param {string} description - Descrição
 * @param {string} id - ID do item
 * @returns {Notification|null}
 */
export const sendHRNotification = (type, title, description, id) => {
    if (!type || !title) return null;

    const emojis = {
        'recruitment': '📋',
        'vacation': '🏖️',
        'attendance': '⏰',
        'onboarding': '👋',
        'offboarding': '👋'
    };

    const emoji = emojis[type] || '👥';
    const routeMap = {
        'recruitment': '/hr/recruitment',
        'vacation': '/hr/vacations',
        'attendance': '/hr/attendance',
        'onboarding': '/hr/onboarding',
        'offboarding': '/hr/offboarding'
    };

    return sendModuleNotification(
        'hr',
        `${emoji} ${title}`,
        (description || '').substring(0, 80) + ((description || '').length > 80 ? '...' : ''),
        {
            route: routeMap[type] || '/hr',
            tag: `hr_${type}_${id || Date.now()}`,
            priority: 'Média',
            dedupeKey: `hr_${type}_${id}`,
            data: { type, title, description, id }
        }
    );
};

// ============================================================
// PROJECT NOTIFICATIONS
// ============================================================

/**
 * Envia notificação de projetos
 * @param {string} type - Tipo (task, milestone, deadline)
 * @param {string} title - Título
 * @param {string} description - Descrição
 * @param {string} projectId - ID do projeto
 * @param {string} id - ID do item
 * @returns {Notification|null}
 */
export const sendProjectNotification = (type, title, description, projectId, id) => {
    if (!type || !title) return null;

    const emojis = {
        'task': '✅',
        'milestone': '🎯',
        'deadline': '⏰',
        'status': '📊',
        'comment': '💬'
    };

    const emoji = emojis[type] || '📋';
    const route = projectId ? `/projects/${projectId}` : '/projects';

    return sendModuleNotification(
        'projects',
        `${emoji} ${title}`,
        (description || '').substring(0, 80) + ((description || '').length > 80 ? '...' : ''),
        {
            route: route,
            tag: `project_${type}_${id || Date.now()}`,
            priority: type === 'deadline' ? 'Alta' : 'Média',
            dedupeKey: `project_${type}_${id}`,
            data: { type, title, description, projectId, id }
        }
    );
};

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Verifica se o usuário pode receber notificações
 * @param {boolean} autoRequest - Se deve solicitar permissão automaticamente
 * @returns {Promise<boolean>}
 */
export const canReceiveNotifications = async (autoRequest = true) => {
    if (!isNotificationSupported()) {
        return false;
    }

    if (Notification.permission === "granted") {
        return true;
    }

    if (Notification.permission === "denied") {
        return false;
    }

    // Permission é 'default'
    if (autoRequest) {
        return await requestNotificationPermission(true);
    }

    return false;
};

/**
 * Obtém o status atual das notificações
 * @returns {Promise<Object>}
 */
export const getNotificationStatus = async () => {
    const status = {
        supported: isNotificationSupported(),
        permission: Notification.permission || 'unknown',
        hasServiceWorker: false,
        hasMessaging: false,
        cacheSize: {
            chat: chatNotificationCache.size,
            cooldown: notificationCooldownCache.size
        }
    };

    // Verificar Service Worker
    if ('serviceWorker' in navigator) {
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            status.hasServiceWorker = registrations.length > 0;
            status.serviceWorkerCount = registrations.length;

            // Verificar se tem o SW do Firebase
            const firebaseSW = registrations.find(reg =>
                reg.active?.scriptURL?.includes('firebase-messaging-sw.js')
            );
            status.hasFirebaseSW = !!firebaseSW;
        } catch (error) {
            console.error('Erro ao verificar Service Worker:', error);
        }
    }

    return status;
};

/**
 * Fecha todas as notificações ativas
 * @param {string} tag - Tag específica para fechar (opcional)
 */
export const closeAllNotifications = async (tag = null) => {
    if (!('Notification' in window)) return;

    try {
        // Fechar notificações do Service Worker
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                // Usar getNotifications se disponível
                if (registration.getNotifications) {
                    const notifications = await registration.getNotifications({ tag });
                    notifications.forEach(notification => notification.close());
                }
            }
        }
    } catch (error) {
        console.error('Erro ao fechar notificações:', error);
    }
};

/**
 * Limpa o cache de deduplicação
 */
export const clearNotificationCache = () => {
    chatNotificationCache.clear();
    notificationCooldownCache.clear();
    console.log('🧹 Notification cache cleared');
};

/**
 * Configura o listener para notificações
 * @param {Function} onNotification - Callback quando notificação é recebida
 * @param {Function} onNotificationClick - Callback quando notificação é clicada
 * @param {Function} onNotificationClose - Callback quando notificação é fechada
 */
export const setupNotificationListeners = (onNotification, onNotificationClick, onNotificationClose) => {
    if (!isNotificationSupported()) return;

    // Listener para notificações recebidas (quando o app está em primeiro plano)
    if (onNotification) {
        // Verificar se o FCM está disponível
        import('../firebaseConfig').then(({ messaging, onMessage }) => {
            if (messaging && onMessage) {
                onMessage(messaging, (payload) => {
                    console.log('📩 Mensagem FCM recebida:', payload);
                    onNotification(payload);

                    // Se tiver dados de notificação, mostrar
                    if (payload.notification) {
                        const { title, body } = payload.notification;
                        const module = payload.data?.module || 'helpdesk';
                        const url = payload.data?.route || getModuleDefaultRoute(module);

                        // Use dedupe key from FCM message ID if available
                        const dedupeKey = payload.messageId || `fcm_${module}_${Date.now()}`;

                        sendModuleNotification(module, title, body, {
                            route: url,
                            data: payload.data || {},
                            priority: payload.data?.priority || 'Média',
                            dedupeKey: dedupeKey
                        });
                    }
                });
            }
        }).catch(error => {
            console.warn('FCM não disponível:', error);
        });
    }
};

// ============================================================
// EXPORTS
// ============================================================

export default {
    // Core
    isNotificationSupported,
    requestNotificationPermission,
    sendBrowserNotification,
    sendModuleNotification,
    sendTestNotification,
    canReceiveNotifications,
    getNotificationStatus,
    closeAllNotifications,
    setupNotificationListeners,
    clearNotificationCache,

    // Helpdesk
    sendNewTicketNotification,
    sendFollowUpNotification,
    sendTicketClosedNotification,
    sendTicketAssignedNotification,

    // Finance
    sendFinanceNotification,

    // Inventory
    sendInventoryNotification,

    // Chat (with deduplication)
    sendChatNotification,

    // Quality
    sendQualityNotification,

    // CRM
    sendCRMNotification,

    // HR
    sendHRNotification,

    // Projects
    sendProjectNotification
};